import {OrbitTheme} from '../../architecture/orbit-theme.interface';
import {makeTheme} from '../../make-theme';
import {ThemeName} from '../../architecture/theme-names';
import {MrGreenIcons} from './icons-registry';
import {createGlobalStyle, css} from "styled-components";
import React from "react";
import {baseColors} from "./base-colors";
import {IThemeDefinition} from "../../../services/theme/theme-definition.interface";
import {createMainContentBaseUIComponents} from "./main-content-base-ui-components";
import {ISdkTheme} from "../../architecture/sections/sdk/sdk-theme.interface";
import {createShellBaseUIComponents} from "./shell-base-ui-components";
import {shellGeneralColors} from "./shell-general-colors";
import {mainContentGeneralColors} from "./main-content-general-colors";
import {IFontPresets} from '../../architecture/sections/font-family.interface';
import {makeMrGreenFontsFamily} from "./make-mrgreen-fonts-family";
import {HeaderArenasStyle} from "../../architecture/sections/theme-layout.interface";
import {SDK_CSS_OVERRIDE} from "../../css-global-classes";
import {MrGreenThemeCustomizer} from "./theme-customizer";
import {ThemeBoxes} from "./theme.boxes";

const GlobalStyles = createGlobalStyle`
  body {
    font-family: "Montserrat", verdana, sans-serif;
    font-weight: 600;
  }
  
  .cgp-pc, .cgp-mobile {
    .cgp-themes-main-cta {
      &:hover {
        &.cgp-modal-button{
            text-transform: lowercase;
            &::first-letter {
                text-transform: uppercase;
            }
        }
      }
      &.cgp-modal-button{
        text-transform: lowercase;
        &::first-letter {
            text-transform: uppercase;
        }
      }
    }   
    .${SDK_CSS_OVERRIDE} .cgp-overlay{
        .cgp-modal-themes-main-title {
            color: ${mainContentGeneralColors.textDark};
            background-color: ${mainContentGeneralColors.brandPrimary};
         }

        .cgp-overlay-content.cgp-theme-property-background-color-base05 {
            background-color: ${mainContentGeneralColors.background};
        }

        .cgp-msg-content .cgp-themes-main-text {
            color:${mainContentGeneralColors.brandPrimaryDark};
        }

        .cgp-themes-main-cta, .cgp-themes-touch-id-auto-enabled-cta {
            color: ${mainContentGeneralColors.textDark};
            background-color: ${mainContentGeneralColors.brandPrimary};
            border-color: ${mainContentGeneralColors.brandPrimary};
            &:hover {
              color: ${mainContentGeneralColors.textDark};
              background-color: ${mainContentGeneralColors.brandPrimary};
              border-color: ${mainContentGeneralColors.brandPrimary};
            }
            &:not(:checked) ~ label {
            background-color: ${mainContentGeneralColors.buttonInfo};
          }
        }

        .cgp-themes-secondary-cta {
            color: ${mainContentGeneralColors.brandSecondaryDark};
            background-color: ${mainContentGeneralColors.background};
            border-color: ${mainContentGeneralColors.brandSecondaryDark};
            &:hover {
              color: ${mainContentGeneralColors.textDark};
              background-color: ${mainContentGeneralColors.brandSecondaryDark};
              border-color: ${mainContentGeneralColors.brandSecondaryDark};
            }
        }

        .cgp-themes-large-text {
            color: ${mainContentGeneralColors.brandPrimaryDark};
        }
      
    }
    
    .session-timer-component-container {
      .session-timer-field,
      .session-timer-description {
        color: ${mainContentGeneralColors.textDark};
      }
    }
    
  }

`;

const fontPresets: IFontPresets = {
  thin: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 100;
  `,
  light: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 400;
  `,
  regular: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 500;
  `,
  medium: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 500;
  `,
  body: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 500;
  `,
  bold: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 700;
  `,
  black: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 800;
  `,
  ultra: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 700;
  `,
  secondary: css`
      font-family: "Montserrat", verdana, sans-serif;
      font-weight: 700;
  `
};

const fonts = makeMrGreenFontsFamily();


const sdkTheme: Partial<ISdkTheme> = {

  colors: {
    main: baseColors.white,
    darkmain: baseColors.black,
    secondary: baseColors.white,
    base01: baseColors.base01Dark,
    base02: baseColors.gray113,
    base03: baseColors.gray033,
    base04: baseColors.base01,
    base05: baseColors.base01Dark,
  },
  fontTypes: {
    Main: fonts.regular,
    Secondary: fonts.bold,
    Type03: fonts.medium,
    Type04: fonts.bold,
    Type05: fonts.regular,
    Type06: fonts.bold,
  }

};

const MrGreenShellTheme: OrbitTheme = makeTheme({
  generalColors: shellGeneralColors,
  fonts: fonts,
  fontPresets,
  baseUIComponents: createShellBaseUIComponents,
  layout: {
    headerArenasStyle: HeaderArenasStyle.Menu
  },
  themeSpecificIcons: MrGreenIcons
});

MrGreenShellTheme.sdk = sdkTheme;
MrGreenShellTheme.globalStyles = (<GlobalStyles />);

const MrGreenContentTheme: OrbitTheme = makeTheme({
  generalColors: mainContentGeneralColors,
  fonts: fonts,
  fontPresets,
  themeSpecificIcons: MrGreenIcons,
  layout: {
    headerArenasStyle: HeaderArenasStyle.Menu
  },
  baseUIComponents: createMainContentBaseUIComponents
})

MrGreenContentTheme.sdk = sdkTheme;
MrGreenShellTheme.components.header.colors.indicatorBadge.backgroundInGridLayout = baseColors.base05;
MrGreenShellTheme.components.newLiveCasino.colors.filterButtonLabel = shellGeneralColors.textLight;
MrGreenContentTheme.components.header.colors.indicatorBadge.backgroundInGridLayout = mainContentGeneralColors.brandSecondaryLight;
MrGreenContentTheme.components.gamesListTitle.colors.text.regular = mainContentGeneralColors.brandSecondaryDark;
MrGreenContentTheme.components.gamesListTitle.colors.text.dailyJackpot = mainContentGeneralColors.brandSecondaryDark;
MrGreenContentTheme.components.gamesListTitle.colors.text.redTigerJackpot = mainContentGeneralColors.brandSecondaryDark;
MrGreenContentTheme.components.generalTitle.colors.background = mainContentGeneralColors.background;

MrGreenContentTheme.components.newLiveCasino.colors.gridItem.infobarBackground = mainContentGeneralColors.backgroundLight;

MrGreenContentTheme.components.gamingHistory.colors.background = baseColors.gray243;
MrGreenContentTheme.components.gamingHistory.colors.recordBackground = baseColors.white;

const themeDefinition: IThemeDefinition = {
  themeName: ThemeName.MrGreen,
  shellTheme: MrGreenShellTheme,
  mainContentTheme: MrGreenContentTheme,
  popupsTheme: MrGreenContentTheme,
  toastersTheme: MrGreenContentTheme,
  useSquareIconsForGames: true,
  createThemeCustomizer: (services) => new MrGreenThemeCustomizer(services),
  themeBoxes: ThemeBoxes
}


export default themeDefinition;